import type { ImageBlock } from '@impact/types';
import { ImageIcon } from 'lucide-react';
import { cn } from '@/lib/utils';

interface ImageBlockRendererProps {
  block: ImageBlock;
  editing?: boolean;
}

/**
 * Renders an image with optional caption and link
 */
export function ImageBlockRenderer({ block, editing }: ImageBlockRendererProps) {
  const {
    src,
    alt,
    caption,
    width = 'full',
    linkUrl,
    aspectRatio = 'video',
    rounded = true,
    shadow = true,
  } = block.data;

  const widthClasses = {
    full: 'w-full',
    wide: 'max-w-4xl mx-auto',
    medium: 'max-w-2xl mx-auto',
    small: 'max-w-md mx-auto',
  };

  const aspectClasses = {
    auto: '',
    video: 'aspect-video',
    square: 'aspect-square',
    '4/3': 'aspect-[4/3]',
  };

  const isAutoAspect = aspectRatio === 'auto';
  const hasSrc = Boolean(src);

  const imageContent = (
    <figure className={cn('my-8', widthClasses[width])}>
      <div
        className={cn(
          'relative overflow-hidden',
          aspectClasses[aspectRatio] || (editing && !hasSrc ? 'aspect-video' : ''),
          rounded && 'rounded-xl',
          shadow && hasSrc && 'shadow-lg',
        )}
      >
        {hasSrc ? (
          /* eslint-disable-next-line @next/next/no-img-element */
          <img
            src={src}
            alt={alt}
            className={cn(
              isAutoAspect ? 'w-full h-auto' : 'absolute inset-0 h-full w-full object-cover',
            )}
          />
        ) : editing ? (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 border-2 border-dashed border-muted-foreground/30 bg-muted/40 text-muted-foreground">
            <ImageIcon className="h-10 w-10 opacity-50" />
            <p className="text-sm">Use the toolbar above to add an image</p>
          </div>
        ) : null}
      </div>
      {caption && (
        <figcaption
          className="mt-3 text-sm text-muted-foreground text-center"
          {...(editing ? { 'data-editable': 'data.caption' } : {})}
        >
          {caption}
        </figcaption>
      )}
    </figure>
  );

  if (linkUrl) {
    if (editing) {
      return <span className="block">{imageContent}</span>;
    }
    return (
      <a href={linkUrl} target="_blank" rel="noopener noreferrer" className="block">
        {imageContent}
      </a>
    );
  }

  return imageContent;
}
